import {
  AbstractMesh,
  Mesh,
  MeshBuilder,
  Tags,
  Texture,
  TransformNode,
  Vector3,
  VertexData,
} from '@babylonjs/core';
import { BabylonjsModule } from '@modules/babylonjs/src/BabylonjsModule';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { clearArchetype, createEntityFactory, GLTFLoader, toRadian } from '@modules/standard';
import { World } from 'miniplex';

import ParkingEnteranceModel from '../../assets/model/facility/ParkingEnterance.glb';
import { MaterialManager, MaterialType } from '../../graphic/material/MaterialManager';
import { LightManager } from '../../LightManager';
import { TourDanjiFetcher } from '../../TourDanjiFetcher';
import { calculateInteriorBound } from '../../utiles/LargestInteriorRectangle';
import { vector3ToFloatArray } from '../../utiles/Utils';
import { MeshType } from '../component/data/DanjiMesh';
import { FacilityType } from '../component/data/Facility';
import { Entity } from '../Entity';

import { GroundSystem } from './GroundSystem';
import { RaycastSystem } from './RaycastSystem';

/**
 * 설명 추가 필요
 *
 * @group System
 * @category System
 */
export class FacilitySystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('FacilitySystem');

  //------------------------------------------------------------------------------------
  // #### System Base ####
  //------------------------------------------------------------------------------------
  private readonly world;
  private readonly facilityArchetype;
  private readonly facilityGUIArchetype;

  private readonly scene;
  private readonly danjiFetcher;
  private readonly gltfLoader;
  private readonly lightManager;
  private readonly groundSystem;
  private readonly materialManager;
  private readonly raycastSystem;
  private readonly createEntity;

  //-----------------------
  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [TourDanjiFetcher.INJECTABLE_TOKEN]: danjiFetcher,
    [GLTFLoader.INJECTABLE_TOKEN]: gltfLoader,
    [RaycastSystem.INJECTABLE_TOKEN]: raycastSystem,
    [LightManager.INJECTABLE_TOKEN]: lightManager,
    [GroundSystem.INJECTABLE_TOKEN]: groundSystem,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: Pick<BabylonjsModule, 'scene'>;
    [TourDanjiFetcher.INJECTABLE_TOKEN]: TourDanjiFetcher;
    [GLTFLoader.INJECTABLE_TOKEN]: GLTFLoader;
    [RaycastSystem.INJECTABLE_TOKEN]: RaycastSystem;
    [LightManager.INJECTABLE_TOKEN]: LightManager;
    [GroundSystem.INJECTABLE_TOKEN]: GroundSystem;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
  }) {
    this.world = world;
    this.scene = babylonjs.scene;
    this.danjiFetcher = danjiFetcher;
    this.gltfLoader = gltfLoader;
    this.lightManager = lightManager;
    this.groundSystem = groundSystem;
    this.raycastSystem = raycastSystem;
    this.materialManager = materialManager;

    this.createEntity = createEntityFactory(this.world);

    this.facilityArchetype = world.with('Facility');
    this.facilityGUIArchetype = world.with('Facility', 'GUIDisplayable');
  }

  dispose() {
    clearArchetype(this.facilityGUIArchetype);
    clearArchetype(this.facilityArchetype);
  }

  private attachComponents(
    type: FacilityType,
    name: string,
    meshes: Mesh[] | AbstractMesh[],
    ext: {
      min: Vector3;
      max: Vector3;
    } = { min: Vector3.One(), max: Vector3.One() }
  ) {
    let components: Partial<Entity> = {};

    let meshType: MeshType | undefined = undefined;
    switch (type) {
      case FacilityType.Building:
        components.GUIDisplayable = {
          position: ext.max.add(ext.min).scale(0.5),
          offset: Vector3.Zero(),
          occludee: true,
          hide: false,
        };
        meshType = MeshType.FACILITY_BUILDING;
        break;
      case FacilityType.Gym:
        meshType = MeshType.FACILITY_GYM;
        break;
      case FacilityType.Parking:
        meshType = MeshType.FACILITY_PARKING;
        break;
      case FacilityType.ParkingEnterance:
        meshType = MeshType.FACILITY_PARKING_ENTERANCE;
        break;
    }

    components.Facility = {
      type: type,
      facilityName: name,
      extends: ext,
    };

    if (meshType) {
      components.DanjiMeshes = {
        danjiMeshes: [
          {
            type: meshType,
            meshes: meshes,
          },
        ],
      };
    }

    const entityComponents: Omit<Entity, 'entityId'> = {
      ...components,
    };

    this.createEntity(entityComponents);
  }

  // --------------------------------
  private get generatable2d() {
    return (
      this.groundSystem.groundMesh && this.danjiFetcher.facility2DDatas && this.danjiFetcher.facility2DDatas.length > 0
    );
  }
  private get generatable3d() {
    return (
      this.groundSystem.groundMesh && this.danjiFetcher.facility3DDatas && this.danjiFetcher.facility3DDatas.length > 0
    );
  }

  // --------------------------------
  // decal
  public generateGym(rootNode: TransformNode) {
    this.generateFacilityDecal(FacilityType.Gym, rootNode, '운동시설', 'Decal_Gym');
  }
  public generateParking(rootNode: TransformNode) {
    this.generateFacilityDecal(FacilityType.Parking, rootNode, '지상주차장', 'Decal_Parking');
  }
  private generateFacilityDecal(type: FacilityType, rootNode: TransformNode, dataName: string, decalName: string) {
    if (!this.generatable2d) return;
    const dataList = this.danjiFetcher.facility2DDatas!.filter((d) => d.name === dataName);

    dataList.forEach((data) => {
      const bound = calculateInteriorBound(data.positions);
      if (!bound) {
        console.error(new Error("Can't calculateInteriorBound. Check the coordinates."));
        return;
      }
      const centerPosition = new Vector3(bound.center.x, 0, bound.center.y);
      const angle = Math.atan2(bound.axis.y, bound.axis.x);
      const adjustFitSize = new Vector3(bound.size.x, bound.size.y, Number.MAX_VALUE);

      const decal = MeshBuilder.CreateDecal(
        `${decalName}${new Vector3(Math.floor(centerPosition.x), Math.floor(centerPosition.z))}`,
        this.groundSystem.getBaseDecalMesh(),
        {
          position: centerPosition,
          normal: Vector3.Up(),
          size: adjustFitSize,
          angle: angle,
        }
      );

      if (type === FacilityType.Gym) {
        decal.material = this.materialManager.createMaterial(MaterialType.Decal_Gym_Pattern);
      } else {
        const roadWidths = adjustFitSize.y;
        const width = roadWidths ? Math.ceil(roadWidths / 2) : 1;
        const material = this.materialManager.createMaterial(MaterialType.Decal_Parking_Pattern, width.toString());

        if (material.emissiveTexture) {
          (material.emissiveTexture as Texture).vScale = width;
        }
        decal.material = material;
      }
      decal.setParent(rootNode);
      decal.position = decal.position.add(new Vector3(0, 0.4, 0));

      this.attachComponents(type, data.name, [decal], decal.getHierarchyBoundingVectors());
    });
  }

  // --------------------------------
  // ParkingEnterance
  public async generateParkingEnterance(parentTransform: TransformNode) {
    if (!this.generatable2d) return;
    const dataList = this.danjiFetcher.facility2DDatas!.filter((d) => d.name === '지하주차장입구');
    if (dataList.length === 0) return;

    const modelLoadOptions = {
      autoCompileShader: true,
      autoLoadTexture: true,
    };
    const modelData = [
      {
        src: ParkingEnteranceModel,
        modelName: 'RootNode',
        parentTransform: parentTransform,
      },
    ];

    const result = await this.gltfLoader.loadModels(this.scene, modelData, undefined, modelLoadOptions);

    const root = result.at(0)?.root;
    if (!root) return;
    root.name = 'ParkingEnterance';

    const firstTransformNode = root.getChildTransformNodes(true)?.at(0);
    if (firstTransformNode) {
      firstTransformNode.rotation = new Vector3(-0.5 * Math.PI, 0, 0.5 * Math.PI);
      firstTransformNode.scaling = new Vector3(-10, 10, 10);
    }

    const meshes = root.getChildMeshes(false);
    meshes.forEach((mesh) => {
      mesh.material?.dispose();
    });
    if (meshes && meshes.length === 4) {
      meshes[0]!.material = this.materialManager.createMaterial(MaterialType.ParkingEnterance_Glass);
      meshes[1]!.material = this.materialManager.createMaterial(MaterialType.ParkingEnterance_Entry);
      meshes[2]!.material = this.materialManager.createMaterial(MaterialType.ParkingEnterance_Frame);
      meshes[3]!.material = this.materialManager.createMaterial(MaterialType.ParkingEnterance_Letter);
    }

    for (let i = 0; i < dataList.length; i++) {
      const data = dataList.at(i);
      if (!data) continue;

      const rotationY = (data.directions.at(0) ?? 0) * toRadian;
      const scaleInPlaces = data.scaleInPlaces.at(0) ?? 1;
      const roadWidths = (data.roadWidths.at(0) ?? 6) / 75;

      for (let j = 0; j < data.positions.length; j++) {
        const position = data.positions.at(j);
        if (!position) continue;

        const target = i === 0 ? root : root.clone(root.name, root.parent);
        if (target) {
          const y = this.raycastSystem.pickWithRayByLand(position.x, position.z);
          target.position = new Vector3(position.x, y?.pickedPoint?.y ?? position.y, position.z);
          target.rotation = new Vector3(0, rotationY, 0);
          target.scaling.addInPlace(new Vector3(scaleInPlaces * roadWidths, scaleInPlaces, scaleInPlaces));
          const meshes = target.getChildMeshes(false);
          this.attachComponents(FacilityType.ParkingEnterance, data.name, meshes, target.getHierarchyBoundingVectors());
        }
      }
    }
  }

  // --------------------------------
  // Building
  public generateFacilityBuilding(rootNode: TransformNode) {
    if (!this.generatable3d) return;

    this.danjiFetcher
      .facility3DDatas!.toSorted((a, b) => a.meta.zOrder - b.meta.zOrder)
      .forEach((data) => {
        const positions = this.danjiFetcher.adjustFloorCoordinates(
          data.coordinates.concat(data.boundaryCoordinates),
          data.meta.floor,
          data.meta.zOrder
        );
        const vd = new VertexData();
        const normals = Array<number>();
        vd.positions = vector3ToFloatArray(positions);
        vd.indices = Array.from(Array(positions.length).keys()).reverse();
        VertexData.ComputeNormals(vd.positions, vd.indices, normals);
        vd.normals = normals;

        const meshes = [];
        const mesh = new Mesh(`${data.name}`);
        vd.applyToMesh(mesh);

        this.lightManager.addShadowCaster(mesh);
        this.lightManager.addShadowCaster(mesh, true);
        mesh.setParent(rootNode);
        mesh.material = this.materialManager.createMaterial(MaterialType.FacilityBuilding);
        mesh.metadata = data.meta;
        meshes.push(mesh);

        if (data.meta.allowRooftopDecal) {
          const topY = positions.reduce((p, c) => (p.y > c.y ? p : c)).y;
          const topPositions = this.adjustFloorTopCoordinates(topY, positions, data.meta.zOrder);
          const topMesh = this.generateBuildingTopMesh(mesh, data.name, topPositions, data.meta.allowRooftopDecal);
          meshes.push(topMesh);
        }

        this.attachComponents(FacilityType.Building, data.name, meshes, mesh.getHierarchyBoundingVectors());
      });
  }

  // --------------------------------
  private adjustFloorTopCoordinates(top: number, coordinates: Vector3[], _zOrder: number) {
    return coordinates.filter((c) => c.y == top);
  }

  private generateBuildingTopMesh(parent: AbstractMesh, id: string, positions: Vector3[], _allowRooftopDecal: boolean) {
    const vd = new VertexData();
    const normals = Array<number>();
    vd.positions = vector3ToFloatArray(positions);
    vd.indices = Array.from(Array(positions.length).keys()).reverse();
    VertexData.ComputeNormals(vd.positions, vd.indices, normals);
    vd.normals = normals;
    // for merging with ground mesh
    const groundVdKinds = this.groundSystem.groundMesh!.getVerticesDataKinds();
    const uvs = groundVdKinds?.filter((x) => x.startsWith('uv')).map((x) => x.replace('uv', 'uvs'));
    const uv = Array<number>((vd.positions?.length ?? 0 / 3) * 2);
    uvs?.forEach((k) => {
      Object.assign(vd, { [k]: uv });
    });

    const m = new Mesh(`${id}_top`);
    m.parent = parent;
    m.metadata = parent.metadata;
    m.material = parent.material;
    vd.applyToMesh(m);
    this.lightManager.addShadowCaster(m);
    this.lightManager.addShadowCaster(m, true);
    Tags.AddTagsTo(m, 'facility top decalable disposeAfterDecal');
    return m;
  }

  readonly operations: SystemOperationDefinition[] = [];
}
